import React, { useState, useEffect, useMemo, Fragment, FC } from 'react'
import { graphql } from 'gatsby'

import {
  Tabs,
  AllocationDoughnut,
  AllocationList,
  Text,
  Grid,
  GridItem,
} from 'mf-ui-components'

import Loading from './Loading'
import Error from './Error'
import { getLocaleFromCountry } from '../../../../utils/locale'
import {
  BreakdownType,
  extractAssetsAllocationData,
  COMPLEXITY_LEVEL,
  RISK_LEVELS,
} from './libs'
import {
  AllocationCategoryContainer,
  AllocationCategoryButton,
  AllocationCategoryButtonsSeparator,
  AssetsBreakdownContainer,
  DoughnutContainer,
  StyledCard,
  TabWrapper,
} from './AssetsBreakdown.styles'
import { KeyToTheFigures } from '../ModelPortfolios/KeyToTheFigures'
import { findModelPortfolio } from '../../../../utils/modelPortfolios'
import { useAssetsBreakdown } from './useAssetsBreakdown'
import {
  InvestmentStyle,
  ModelPortfolio,
  ModelPortfolioWithAssetsBreakdown,
  RiskLevel,
} from '../../../../types/ModelPortfolio'
import type { AssetAllocationInfo } from '../../../../types/ModelPortfolio'
import { InvestmentTypeControl } from './InvestmentTypeControl'
import { BaseBlockProps } from '../../../../types/BaseBlockProps'
import { ASSETS_BREAKDOWN_TYPES } from './data'
import { l10nCopy } from '../../../../utils/copy/l10nCopy'

const country = process.env.GATSBY_COUNTRY

const AssetsBreakdown: FC<BaseBlockProps> = ({ marginBottom, hiddenAt }) => {
  const locale = getLocaleFromCountry(country)

  const [breakdownType, setBreakdownType] = useState<BreakdownType>(
    ASSETS_BREAKDOWN_TYPES[0]
  )
  const [riskLevel, setRiskLevel] = useState<RiskLevel>(4)
  const [currentModelPortfolio, setCurrentModelPortfolio] =
    useState<ModelPortfolioWithAssetsBreakdown>()
  const [investmentStyle, setInvestmentStyle] =
    useState<InvestmentStyle>('classic')

  const assetAllocation: AssetAllocationInfo[] | undefined = useMemo(
    () =>
      currentModelPortfolio &&
      extractAssetsAllocationData(currentModelPortfolio, breakdownType, locale),
    [currentModelPortfolio, locale, breakdownType]
  )

  const { isLoaded, error, assetsBreakdownData } = useAssetsBreakdown()

  useEffect(() => {
    if (!assetsBreakdownData) return

    const defaultModelPortfolio = assetsBreakdownData.find(
      findModelPortfolio(country!, riskLevel, COMPLEXITY_LEVEL, investmentStyle)
    )

    if (!defaultModelPortfolio) return

    setCurrentModelPortfolio(defaultModelPortfolio)
  }, [assetsBreakdownData])

  useEffect(() => {
    if (!assetsBreakdownData || !riskLevel || !investmentStyle) return

    const allowedModelPortfolios = assetsBreakdownData.filter(
      (remotePortfolioData: Pick<ModelPortfolio, 'riskLevel'>) =>
        RISK_LEVELS.includes(remotePortfolioData.riskLevel)
    )

    const modelPortfolio = allowedModelPortfolios.find(
      findModelPortfolio(country!, riskLevel, COMPLEXITY_LEVEL, investmentStyle)
    )

    if (!modelPortfolio) return

    setRiskLevel(riskLevel)

    setCurrentModelPortfolio(modelPortfolio)
  }, [riskLevel, investmentStyle])

  const isLoading =
    !isLoaded ||
    !currentModelPortfolio ||
    !assetsBreakdownData ||
    !assetAllocation

  if (error) return <Error />

  return (
    <AssetsBreakdownContainer marginBottom={marginBottom} hiddenAt={hiddenAt}>
      <StyledCard>
        <Tabs
          stretch
          destroyInactiveTabPane
          defaultActiveKey={riskLevel.toString()}
          onChange={(value: string) => setRiskLevel(Number(value) as RiskLevel)}
        >
          {RISK_LEVELS.map((portfolio) => (
            <Tabs.TabPane key={portfolio} tab={portfolio}>
              <Grid
                style={{ maxWidth: '100%', margin: '0 0 24px 0' }}
                justifyContent="space-between"
              >
                <GridItem size={6} style={{ padding: 0 }}>
                  <Text el="span" styleName="bodyRegular" variant="secondary">
                    {l10nCopy('low_risk')}
                  </Text>
                </GridItem>
                <GridItem size={6} style={{ textAlign: 'right', padding: 0 }}>
                  <Text el="span" styleName="bodyRegular" variant="secondary">
                    {l10nCopy('high_risk')}
                  </Text>
                </GridItem>
              </Grid>

              <InvestmentTypeControl
                investmentStyle={investmentStyle}
                handleChange={() =>
                  setInvestmentStyle((previous) =>
                    previous === 'classic' ? 'esg' : 'classic'
                  )
                }
              />

              {isLoading ? (
                <Loading />
              ) : (
                <TabWrapper>
                  <div style={{ marginTop: '32px' }}>
                    <Text el="h4" styleName="title7">
                      {l10nCopy('assets_breakdown.title')}
                    </Text>
                    <Grid>
                      <Grid
                        size={{ sm: 12, md: 5 }}
                        style={{ margin: '32px 0' }}
                      >
                        <DoughnutContainer>
                          <AllocationDoughnut
                            assets={assetAllocation}
                            options={{
                              cutout: '75%',
                              aspectRatio: 1,
                              maintainAspectRatio: false,
                              responsive: true,
                              hover: { mode: undefined },
                              plugins: { tooltip: { enabled: false } },
                            }}
                          />
                        </DoughnutContainer>
                      </Grid>
                      <Grid
                        flow="column"
                        alignItems="stretch"
                        gap="3xl"
                        size={{ sm: 12, md: 7 }}
                        style={{ margin: '32px 0' }}
                      >
                        <Grid flow="row">
                          <AllocationCategoryContainer>
                            {ASSETS_BREAKDOWN_TYPES.map((tab, tabIndex) => (
                              <Fragment key={tabIndex}>
                                <AllocationCategoryButton
                                  onClick={() => setBreakdownType(tab)}
                                  isActive={breakdownType === tab}
                                >
                                  <p>
                                    {l10nCopy(
                                      `portfolio_asset_class_types.${tab}`
                                    )}
                                  </p>
                                </AllocationCategoryButton>

                                {tabIndex <
                                  ASSETS_BREAKDOWN_TYPES.length - 1 && (
                                  <AllocationCategoryButtonsSeparator />
                                )}
                              </Fragment>
                            ))}
                          </AllocationCategoryContainer>
                        </Grid>

                        <AllocationList assets={assetAllocation} />
                      </Grid>
                    </Grid>
                  </div>

                  <KeyToTheFigures />
                </TabWrapper>
              )}
            </Tabs.TabPane>
          ))}
        </Tabs>
      </StyledCard>
    </AssetsBreakdownContainer>
  )
}

export default AssetsBreakdown

export const query = graphql`
  fragment assetsBreakdownFields on WPGraphQL_MfblocksAssetsBreakdownBlock {
    name
    attributes {
      marginBottom
      hiddenAt
    }
  }
`
